/**
 * Configurações → Distribuição de refeições.
 * Padrões de % de calorias por refeição, por programa e nº de refeições. O
 * padrão do SISTEMA é só leitura; salvar cria a versão da nutricionista (que
 * passa a valer no gerador). "Voltar ao padrão do sistema" apaga a versão dela.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { distribuicaoRefeicoesService } from '../../services/api';
import { mealIcon } from '../PlanoAlimentar/helpers';
import { useToast } from '../../contexts/ToastContext';
import { Ico, emo, noEmo } from '../ui/Ico';

const BRAND = '#D4A574';
const S = {
  wrap: { padding: '1.5rem', maxWidth: 900, margin: '0 auto' },
  card: { background: '#ffffff', border: '1.5px solid #ebe3da', borderRadius: 14, padding: '1.25rem', marginBottom: '1rem' },
  label: { fontSize: '0.8rem', fontWeight: 600, color: '#5a3e2b', display: 'block', marginBottom: 4 },
  input: { border: '1.5px solid #ebe3da', borderRadius: 8, padding: '0.45rem 0.6rem', fontSize: '0.88rem', fontFamily: 'inherit' },
  btn: { border: 'none', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' },
};

export default function DistribuicaoRefeicoes({ onNavigate }) {
  const { showSuccess, showError } = useToast();
  const [dados, setDados]       = useState(null);
  const [programa, setPrograma] = useState('');
  const [n, setN]               = useState(5);
  const [linhas, setLinhas]     = useState([]);
  const [salvando, setSalvando] = useState(false);

  const carregar = () => distribuicaoRefeicoesService.list()
    .then(r => { if (r.success) setDados(r.data); else showError(r.error || 'Erro ao carregar'); })
    .catch(() => showError('Erro ao carregar as distribuições'));
  // Carrega uma vez ao abrir a tela
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  // Distribuição efetiva: minha (programa) > minha (geral) > sistema (programa) > sistema (geral)
  const efetiva = useMemo(() => {
    const ds = dados?.distribuicoes || [];
    const pega = (orig, prog) => ds.find(d => d.origem === orig && d.programa === prog && d.num_refeicoes === n);
    return pega('minha', programa) || (programa && pega('minha', '')) || pega('sistema', programa) || (programa && pega('sistema', '')) || null;
  }, [dados, programa, n]);
  const ehMinhaExata = efetiva?.origem === 'minha' && efetiva.programa === programa;

  useEffect(() => { setLinhas(efetiva ? efetiva.refeicoes.map(r => ({ ...r })) : []); }, [efetiva]);

  const soma = linhas.reduce((t, r) => t + (Number(r.pct_kcal) || 0), 0);
  const somaOk = Math.abs(soma - 100) < 0.05;
  const setPct = (i, v) => setLinhas(l => l.map((r, j) => (j === i ? { ...r, pct_kcal: v === '' ? '' : Number(v) } : r)));
  const setNome = (i, v) => setLinhas(l => l.map((r, j) => (j === i ? { ...r, nome: v } : r)));

  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await distribuicaoRefeicoesService.save(programa, linhas);
      if (r.success) { showSuccess('Distribuição salva — já vale no gerador de planos.'); await carregar(); }
      else showError(r.error || 'Erro ao salvar');
    } catch (e) { showError(e?.response?.data?.error || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };
  const restaurar = async () => {
    if (!window.confirm('Apagar a sua versão e voltar ao padrão do sistema?')) return;
    const r = await distribuicaoRefeicoesService.restore(programa, n);
    if (r.success) { showSuccess('Voltou ao padrão do sistema.'); carregar(); } else showError(r.error || 'Erro');
  };

  const origemTxt = !efetiva ? 'Sem distribuição para esta combinação'
    : efetiva.origem === 'minha' ? (efetiva.programa === programa ? 'Sua versão' : 'Sua versão geral (todos os programas)')
    : (efetiva.programa === programa && programa ? 'Padrão do sistema para este programa' : 'Padrão geral do sistema');

  return (
    <div style={S.wrap}>
      <button onClick={() => onNavigate('configuracoes')} style={{ ...S.btn, background: 'none', color: '#736b64', padding: 0, marginBottom: '0.75rem' }}>← Configurações</button>
      <h1 style={{ fontSize: '1.4rem', color: '#2f2a26', margin: '0 0 0.25rem' }}><Ico e="🍽" sp />Distribuição de refeições</h1>
      <p style={{ color: '#736b64', fontSize: '0.88rem', marginTop: 0 }}>
        Percentual das calorias do dia em cada refeição, usado como ponto de partida ao gerar planos.
        No momento de montar o plano você ainda pode mudar tudo, caso a caso.
      </p>

      <div style={{ ...S.card, display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ flex: '1 1 260px' }}>
          <span style={S.label}>Programa</span>
          <select style={{ ...S.input, width: '100%' }} value={programa} onChange={e => setPrograma(e.target.value)}>
            <option value="">Todos os programas (padrão geral)</option>
            {Object.entries(dados?.programas || {}).map(([id, nome]) => <option key={id} value={id}>{noEmo(nome)}</option>)}
          </select>
        </label>
        <div>
          <span style={S.label}>Nº de refeições</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[3, 4, 5, 6, 7].map(q => (
              <button key={q} onClick={() => setN(q)} aria-pressed={n === q}
                style={{ ...S.btn, padding: '0.45rem 0.75rem', background: n === q ? BRAND : '#f6f0ea', color: n === q ? '#ffffff' : '#5a3e2b' }}>{q}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={S.card}>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: '#736b64' }}>{origemTxt}</p>
        {linhas.length === 0 ? (
          <p style={{ color: '#736b64' }}>Não há distribuição cadastrada para {n} refeições.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead><tr style={{ textAlign: 'left', color: '#736b64' }}>
              <th scope="col" style={{ padding: 6 }}>Refeição</th><th scope="col" style={{ padding: 6 }}>Horário</th>
              <th scope="col" style={{ padding: 6, width: 110 }}>% das kcal</th></tr></thead>
            <tbody>
              {linhas.map((r, i) => (
                <tr key={r.chave} style={{ borderTop: '1px solid #ebe3da' }}>
                  <td style={{ padding: 6 }}>
                    <span aria-hidden="true">{emo(mealIcon(r.chave))} </span>
                    <input style={S.input} value={r.nome} maxLength={40} aria-label={`Nome da refeição ${i + 1}`} onChange={e => setNome(i, e.target.value)} />
                  </td>
                  <td style={{ padding: 6, color: '#736b64' }}>{r.horario || '—'}</td>
                  <td style={{ padding: 6 }}>
                    <input type="number" min="2" max="60" style={{ ...S.input, width: 80 }} value={r.pct_kcal}
                      aria-label={`Percentual de ${r.nome}`} onChange={e => setPct(i, e.target.value)} /> %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p aria-live="polite" style={{ margin: '0.75rem 0', fontSize: '0.85rem', color: somaOk ? '#2f5236' : '#8a5f1a' }}>
          Soma: {Math.round(soma * 10) / 10}% {emo(somaOk ? '✓' : '— precisa ser 100%')}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={salvar} disabled={!somaOk || salvando || !linhas.length}
            style={{ ...S.btn, background: somaOk ? BRAND : '#d9d0c6', color: '#ffffff' }}>
            {salvando ? 'Salvando…' : ehMinhaExata ? 'Salvar alterações' : 'Salvar como minha versão'}
          </button>
          {ehMinhaExata && (
            <button onClick={restaurar} style={{ ...S.btn, background: '#ffffff', color: '#736b64', border: '1.5px solid #ebe3da' }}>
              ↺ Voltar ao padrão do sistema
            </button>
          )}
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: '#736b64' }}>
        Os padrões do sistema são iniciais e precisam de validação clínica. Adicionar ou remover refeições é feito no momento de montar cada plano.
      </p>
    </div>
  );
}
